import React, { FC } from 'react';
import { truncate } from '@blink/components/src/utils';
import { ReactComponent as CopyIcon } from '@blink/assets/src/icons/copy.svg';
import { Link, useNavigate } from 'react-router-dom';
import { formatUnits } from 'ethers';
import { useSelector } from 'react-redux';

import { UserBorrowItem } from '../UserBorrowsSection';
import { UserBorrowsCardSkeleton } from './UserBorrowsCardSkeleton';

import { BorrowStatusType } from '@/shared/api/borrow';
import { cn } from '@/shared/lib/tailwind-merge';
import { AssetIcon, Button, ButtonVariant, SkeletonInfo, TooltipWrapper } from '@/shared/ui';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';
import { Store } from '@/shared/store';
import { useGetRiskLevel } from '@/shared/hooks/useGetRiskLevel';
import { formatWithCommas } from '@/shared/utils/helpers';

type UserBorrowsCard = UserBorrowItem & {
    connectedMarginAccount?: string;
    status: BorrowStatusType;
};

export const UserBorrowsCard: FC<UserBorrowsCard> = ({
    address,
    amount,
    decimals,
    maintenance,
    history,
    symbol,
    connectedMarginAccount,
    status,
    agreementAddress,
}) => {
    const navigate = useNavigate();
    const isBorrowConnected = connectedMarginAccount === address;
    const handleCopyAddress = useCopyToClipboard(address);
    const connectedTitle = () => {
        if (!isBorrowConnected) {
            return;
        }
        return (
            <div className='flex items-center justify-end text-primary'>
                Connected with WalletConnect
            </div>
        );
    };

    const riskFactor =
        maintenance?.riskFactor || history?.[history.length - 1]?.data?.riskFactor || '0';

    const accruedInterest =
        maintenance?.accruedInterest || history?.[history.length - 1]?.data?.accruedInterest || '0';

    const stressTested =
        maintenance?.stressTested || history?.[history.length - 1]?.data?.stressTested || '0';

    const { agreements } = useSelector((state: Store) => ({
        agreements: state.agreements.borrowerAgreements,
    }));

    const agreement = agreements.find((agreement) => agreement.address === agreementAddress)!;

    if (!agreement) {
        return (
            <UserBorrowsCardSkeleton
                address={address}
                symbol={symbol}
                connectedMarginAccount={connectedMarginAccount}
                status={status}
            />
        );
    }

    const { isHigh, isWarning, isLow, liquidationThreshold, marginCallThreshold } = useGetRiskLevel(
        {
            health: riskFactor,
            marginCallThreshold: agreement.marginCallThreshold,
            liquidationThreshold: agreement.liquidationThreshold,
        },
    );

    const riskColor = isHigh
        ? 'bg-[var(--Red-50,#FFF0F3)] text-red-800'
        : isWarning
          ? 'bg-[var(--Yellow-50,#FFFAE0)] text-yellow-800'
          : isLow
            ? 'bg-[var(--Green-50,#E1FCEF)] text-green-800'
            : '';

    const state = isHigh ? 'high' : isWarning ? 'rising' : isLow ? 'low' : '';

    const handleDetails = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/pool/${agreement.address}?role=borrower`);
    };

    return (
        <Link
            to={
                status === BorrowStatusType.OPENED &&
                riskFactor != '0' &&
                stressTested != '0' &&
                accruedInterest != '0'
                    ? `/borrow/${address}`
                    : ''
            }
            state={{
                marginCallThreshold,
                liquidationThreshold,
            }}
            className={cn(
                `
                    min-w-[362px]
                    rounded 
                    border border-opaque 
                    text-content-secondary 
                    transition-shadow duration-300 ease-in-out 
                    hover:text-content-secondary 
                    hover:shadow-[0_0_0_0px_var(--color-border-opaque),0_0_0_2px_var(--color-border-accent)]
                    flex py-3 px-5 flex-col items-start flex-shrink-0
                `,
                isBorrowConnected && 'border border-opaque',
                status !== BorrowStatusType.OPENED &&
                    'border border-opaque hover:border-opaque hover:cursor-auto',
            )}
        >
            <div className='flex py-3 px-0 justify-between items-start self-stretch'>
                <div className='flex gap-3'>
                    <AssetIcon assetName={symbol} className='w-8 h-8' />
                    <div>
                        <div className='text-content-primary text-[14px] font-semibold leading-5'>
                            {symbol}
                        </div>
                        <div className='flex text-[12px] font-normal justify-center items-center gap-2'>
                            {truncate({ value: address, startSymbols: 6 })}
                            <Button
                                variant={ButtonVariant.Ghost}
                                onClick={handleCopyAddress}
                                className='p-0 h-5'
                            >
                                <CopyIcon className='!w-[16px] !h-[16px]' />
                            </Button>
                        </div>
                    </div>
                </div>
                {riskFactor === '0' ? (
                    <SkeletonInfo />
                ) : (
                    <div
                        className={`flex min-w-[24px] min-h-[24px] py-[2px] px-[6px] gap-[4px] justify-center items-center rounded-[320px] ${riskColor}`}
                    >
                        <TooltipWrapper
                            text={`Risk factor shows how close you are to liquidation. Above ${marginCallThreshold} = rising risk. At ${liquidationThreshold} = forced liquidation. Your current risk factor is ${formatWithCommas(Number(riskFactor).toFixed(2))} = ${state} risk.`}
                        />
                        <div className='font-semibold text-[12px]'>
                            {formatWithCommas(
                                Number(riskFactor)
                                    .toFixed(8)
                                    .replace(/\.?0+$/, ''),
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className='flex justify-between py-3 items-start self-stretch gap-3'>
                <div className='flex flex-col text-sm items-start gap-[2px] flex-[1_0_0]'>
                    <div className='text-content-fourth text-sm font-normal'>Maint. Margin</div>
                    <div className='text-content-primary text-md font-semibold'>
                        {stressTested === '0' ? (
                            <SkeletonInfo />
                        ) : (
                            formatWithCommas(
                                Number(formatUnits(stressTested, decimals))
                                    .toFixed(Math.min(decimals, 8))
                                    .replace(/\.?0+$/, ''),
                            )
                        )}
                    </div>
                </div>

                <div className='flex flex-col text-sm items-start gap-[2px] flex-[1_0_0]'>
                    <div className='text-content-fourth text-sm font-normal'>Borrowed Amount</div>
                    <div className='text-content-primary text-md font-semibold'>
                        {formatWithCommas(
                            Number(formatUnits(amount, decimals))
                                .toFixed(Math.min(decimals, 8))
                                .replace(/\.?0+$/, ''),
                        )}
                    </div>
                </div>

                <div className='flex flex-col text-sm items-start gap-[2px] flex-[1_0_0]'>
                    <div className='text-content-fourth text-sm font-normal'>Interest Accrued</div>
                    <div className='text-content-primary text-md font-semibold'>
                        {accruedInterest === '0' ? (
                            <SkeletonInfo />
                        ) : (
                            Number(formatUnits(accruedInterest, decimals))
                                .toFixed(Math.min(decimals, 8))
                                .replace(/\.?0+$/, '')
                        )}
                    </div>
                </div>
            </div>
            <div className='flex py-3 items-start gap-7 self-stretch'>
                <div className='flex flex-col items-start gap-[2px] flex-[1_0_0]'>
                    <div className='text-content-fourth text-sm font-normal'>Pool</div>
                    <Button
                        variant={ButtonVariant.Link}
                        onClick={handleDetails}
                        className='pl-0 py-0 text-content-primary text-md font-semibold hover:underline'
                    >
                        {agreement.title}
                    </Button>
                </div>
            </div>

            {connectedTitle()}
        </Link>
    );
};
